import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, X, Send, Ban } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUS = {
  pending: ['Pendiente', 'bg-amber-100 text-amber-800'],
  fulfilling: ['Surtiendo…', 'bg-amber-100 text-amber-800'],
  fulfilled: ['Surtida', 'bg-emerald-100 text-emerald-800'],
  cancelled: ['Cancelada', 'bg-slate-200 text-slate-600'],
  rejected: ['Rechazada', 'bg-rose-100 text-rose-700'],
};
const fmtDate = (iso) => new Date(iso).toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' });

export const TransferRequests = ({ myBranch, branches, products, onCountChange, onFulfilled }) => {
  const [view, setView] = useState('incoming');
  const [incoming, setIncoming] = useState([]);
  const [mine, setMine] = useState([]);
  const [fulfilling, setFulfilling] = useState(null);

  const load = useCallback(async () => {
    const get = (d) => axios.get(`${API}/transfer-requests?direction=${d}&include_history=true`, { withCredentials: true }).then(r => r.data).catch(() => []);
    const [inc, out] = await Promise.all([get('incoming'), get('outgoing')]);
    setIncoming(inc); setMine(out);
    onCountChange?.(inc.filter(r => r.status === 'pending').length);
  }, [onCountChange]);

  useEffect(() => { load(); }, [load]);

  const act = async (url, body, okMsg) => {
    try {
      await axios.post(`${API}${url}`, body, { withCredentials: true });
      toast.success(okMsg); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
  };

  const views = [
    { id: 'incoming', label: '📥 Me piden', count: incoming.filter(r => r.status === 'pending').length },
    { id: 'mine', label: '📤 Mis peticiones', count: 0 },
    { id: 'new', label: '➕ Nueva petición', count: 0 },
  ];

  return (
    <div className="space-y-4" data-testid="transfer-requests">
      <div className="flex gap-1 bg-slate-100 rounded-lg p-1 w-fit">
        {views.map(v => (
          <button key={v.id} onClick={() => setView(v.id)} data-testid={`req-view-${v.id}`}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${view === v.id ? 'bg-white shadow text-sky-700' : 'text-slate-500'}`}>
            {v.label}{v.count > 0 && <span className="ml-1 bg-rose-500 text-white text-[10px] px-1.5 rounded-full">{v.count}</span>}
          </button>
        ))}
      </div>

      {view === 'incoming' && (
        <RequestList requests={incoming} empty="Ninguna sucursal te ha pedido mercancía" side="incoming"
          onFulfill={setFulfilling}
          onReject={(r) => {
            const reason = window.prompt(`Motivo para rechazar ${r.folio} (opcional):`);
            if (reason === null) return;
            act(`/transfer-requests/${r.id}/reject`, { reason }, 'Petición rechazada');
          }} />
      )}
      {view === 'mine' && (
        <RequestList requests={mine} empty="No has hecho peticiones" side="mine"
          onCancel={(r) => window.confirm(`¿Cancelar petición ${r.folio}?`) && act(`/transfer-requests/${r.id}/cancel`, {}, 'Petición cancelada')} />
      )}
      {view === 'new' && (
        <NewRequestForm myBranch={myBranch} branches={branches} products={products}
          onCreated={() => { load(); setView('mine'); }} />
      )}

      {fulfilling && (
        <FulfillModal request={fulfilling} products={products} onClose={() => setFulfilling(null)}
          onDone={() => { setFulfilling(null); load(); onFulfilled?.(); }} />
      )}
    </div>
  );
};

function RequestList({ requests, empty, side, onFulfill, onReject, onCancel }) {
  if (!requests.length) return <div className="text-center text-sm text-slate-400 py-10">{empty}</div>;
  return (
    <div className="space-y-2">
      {requests.map(r => {
        const [label, cls] = STATUS[r.status] || [r.status, 'bg-slate-100'];
        return (
          <div key={r.id} className={`bg-white border rounded-xl p-3 ${r.status === 'pending' ? 'border-amber-300' : ''}`} data-testid={`request-${r.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="font-mono text-xs text-slate-500">{r.folio}</div>
                <div className="text-sm font-semibold">
                  {side === 'incoming' ? `${r.from_branch_name} te pide` : `Pediste a ${r.to_branch_name}`} · {r.total_qty} pzas
                </div>
                <div className="text-[11px] text-slate-400">{fmtDate(r.created_at)} · {r.created_by_name}</div>
              </div>
              <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${cls}`} data-testid={`request-status-${r.id}`}>{label}</span>
            </div>
            <ul className="mt-2 text-xs text-slate-600 space-y-0.5">
              {r.items.map(i => (
                <li key={i.product_id} className="flex justify-between gap-2">
                  <span className="truncate">{i.product_name}</span>
                  <span className="font-semibold whitespace-nowrap">
                    {i.sent_qty !== null && i.sent_qty !== undefined ? `${i.sent_qty} / ${i.requested_qty}` : i.requested_qty}
                  </span>
                </li>
              ))}
            </ul>
            {r.notes && <div className="mt-1 text-xs italic text-slate-500">📝 {r.notes}</div>}
            {r.transfer_folio && <div className="mt-1 text-xs text-emerald-700">Transferencia: {r.transfer_folio}</div>}
            {r.reason && <div className="mt-1 text-xs text-rose-600">Motivo: {r.reason}</div>}
            {r.status === 'pending' && (
              <div className="mt-3 flex gap-2 justify-end">
                {side === 'incoming' ? (
                  <>
                    <button onClick={() => onReject(r)} className="px-3 py-1.5 text-xs rounded-lg border border-rose-300 text-rose-600 hover:bg-rose-50" data-testid={`request-reject-${r.id}`}>
                      <Ban className="w-3.5 h-3.5 inline mr-1" />Rechazar
                    </button>
                    <button onClick={() => onFulfill(r)} className="px-3 py-1.5 text-xs rounded-lg bg-sky-600 text-white hover:bg-sky-700" data-testid={`request-fulfill-${r.id}`}>
                      <Send className="w-3.5 h-3.5 inline mr-1" />Surtir
                    </button>
                  </>
                ) : (
                  <button onClick={() => onCancel(r)} className="px-3 py-1.5 text-xs rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-50" data-testid={`request-cancel-${r.id}`}>
                    Cancelar petición
                  </button>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function FulfillModal({ request, products, onClose, onDone }) {
  const stockOf = (id) => products.find(p => p.id === id)?.stock ?? 0;
  const [qty, setQty] = useState(() => Object.fromEntries(
    request.items.map(i => [i.product_id, Math.max(0, Math.min(i.requested_qty, stockOf(i.product_id)))])
  ));
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/transfer-requests/${request.id}/fulfill`, {
        items: request.items.map(i => ({ product_id: i.product_id, quantity: parseInt(qty[i.product_id] || 0, 10) })),
        notes: notes.trim() || null,
      }, { withCredentials: true });
      toast.success(`Transferencia ${data.transfer.folio} creada`);
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al surtir');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="fulfill-modal">
      <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-4 space-y-3">
        <div className="flex justify-between items-start">
          <div>
            <h3 className="font-bold">Surtir petición {request.folio}</h3>
            <p className="text-xs text-slate-500">Para {request.from_branch_name}. Pon 0 en lo que no tengas.</p>
          </div>
          <button onClick={onClose} data-testid="fulfill-close"><X className="w-5 h-5" /></button>
        </div>
        {request.items.map(i => (
          <div key={i.product_id} className="flex items-center gap-2 border rounded p-2">
            {i.image_url && <img src={i.image_url} alt="" className="w-10 h-10 rounded object-cover" />}
            <div className="flex-1 min-w-0">
              <div className="text-sm truncate">{i.product_name}</div>
              <div className="text-[10px] text-slate-400">Piden {i.requested_qty} · tu stock: {stockOf(i.product_id)}</div>
            </div>
            <input type="number" min="0" value={qty[i.product_id]}
              onChange={e => setQty(q => ({ ...q, [i.product_id]: e.target.value }))}
              className="w-20 px-2 py-1 border rounded text-sm" data-testid={`fulfill-qty-${i.product_id}`} />
          </div>
        ))}
        <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} placeholder="Notas (opcional)"
          className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="fulfill-notes" />
        <button onClick={submit} disabled={saving}
          className="w-full py-2.5 bg-sky-600 hover:bg-sky-700 text-white rounded-xl font-medium disabled:opacity-40"
          data-testid="fulfill-submit">
          {saving ? 'Creando transferencia…' : 'Enviar transferencia'}
        </button>
      </div>
    </div>
  );
}

function NewRequestForm({ myBranch, branches, products, onCreated }) {
  const others = branches.filter(b => b.id !== myBranch?.branch_id);
  const [supplier, setSupplier] = useState('');
  const [filter, setFilter] = useState('');
  const [items, setItems] = useState([]);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (!supplier && others[0]) setSupplier(others[0].id); }, [others, supplier]);

  const q = filter.toLowerCase();
  const results = filter ? products.filter(p => !p.parent_product_id &&
    (p.name?.toLowerCase().includes(q) || p.sku?.toLowerCase().includes(q))).slice(0, 12) : [];

  const add = (p) => {
    if (!items.some(i => i.product_id === p.id)) setItems(prev => [...prev, { product_id: p.id, product_name: p.name, image_url: p.image_url, quantity: 1 }]);
    setFilter('');
  };

  const submit = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/transfer-requests`, {
        to_branch_id: supplier,
        items: items.map(i => ({ product_id: i.product_id, quantity: parseInt(i.quantity || 0, 10) })),
        notes: notes.trim() || null,
      }, { withCredentials: true });
      toast.success('Petición enviada');
      setItems([]); setNotes(''); onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al enviar');
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="new-request-form">
      <div className="bg-white border rounded-xl p-4">
        <label className="block text-xs font-semibold text-slate-600 mb-1">Pedir a la sucursal</label>
        <select value={supplier} onChange={e => setSupplier(e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="request-supplier-select">
          {others.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
      </div>
      <div className="bg-white border rounded-xl p-4">
        <label className="block text-xs font-semibold text-slate-600 mb-1">Agregar producto</label>
        <input value={filter} onChange={e => setFilter(e.target.value)} placeholder="Buscar por nombre o SKU..."
          className="w-full px-3 py-2 border rounded-lg text-sm mb-2" data-testid="request-product-search" />
        {filter && (
          <div className="max-h-48 overflow-y-auto border rounded divide-y">
            {results.map(p => (
              <button key={p.id} onClick={() => add(p)} className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center gap-2 text-sm" data-testid={`request-add-${p.id}`}>
                {p.image_url && <img src={p.image_url} alt="" className="w-8 h-8 rounded object-cover" />}
                <span className="flex-1 truncate">{p.name}</span>
                <span className="text-xs text-slate-400">aquí: {p.stock ?? 0}</span>
              </button>
            ))}
            {results.length === 0 && <div className="p-3 text-xs text-slate-400 text-center">Sin resultados</div>}
          </div>
        )}
      </div>
      {items.length > 0 && (
        <div className="bg-white border rounded-xl p-4 space-y-2">
          <div className="text-xs font-semibold text-slate-600">Lista ({items.length})</div>
          {items.map((it, idx) => (
            <div key={it.product_id} className="flex items-center gap-2 border rounded p-2">
              {it.image_url && <img src={it.image_url} alt="" className="w-10 h-10 rounded object-cover" />}
              <div className="flex-1 text-sm truncate">{it.product_name}</div>
              <input type="number" min="1" value={it.quantity}
                onChange={e => setItems(prev => prev.map((x, i) => i === idx ? { ...x, quantity: e.target.value } : x))}
                className="w-20 px-2 py-1 border rounded text-sm" data-testid={`request-qty-${it.product_id}`} />
              <button onClick={() => setItems(prev => prev.filter((_, i) => i !== idx))} className="text-rose-500"><X className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      )}
      <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} placeholder="Notas (opcional)"
        className="w-full px-3 py-2 border rounded-lg text-sm bg-white" data-testid="request-notes" />
      <button onClick={submit} disabled={saving || !items.length || !supplier}
        className="w-full py-3 bg-sky-600 hover:bg-sky-700 text-white rounded-xl font-medium disabled:opacity-40 flex items-center justify-center gap-2"
        data-testid="submit-request">
        <Plus className="w-5 h-5" />{saving ? 'Enviando…' : 'Enviar petición'}
      </button>
    </div>
  );
}
